import { useState, useEffect } from 'react'
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { Space, Booking } from '@/types'
import {
    Search,
    Calendar as CalendarIcon,
    Users,
    ArrowRight,
    Info,
    LayoutGrid,
    Clock
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { RESOURCE_TYPE_LABELS } from '@/utils/constants'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import { ptBR } from 'date-fns/locale'

export default function BookingFilterPage() {
    const { currentOrg } = useAuthStore()
    const [loading, setLoading] = useState(false)
    const [spaces, setSpaces] = useState<Space[]>([])
    const [availableSpaces, setAvailableSpaces] = useState<Space[]>([])

    // Filtros
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date())
    const [startTime, setStartTime] = useState("09:00")
    const [endTime, setEndTime] = useState("18:00")
    const [capacity, setCapacity] = useState(1)
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([])

    useEffect(() => {
        if (currentOrg) {
            loadAllSpaces()
        }
    }, [currentOrg?.id])

    const loadAllSpaces = async () => {
        if (!currentOrg) return
        try {
            const q = query(collection(db, 'spaces'), where('orgId', '==', currentOrg.id))
            const snapshot = await getDocs(q)
            setSpaces(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Space)))
        } catch (error) {
            console.error("Erro ao carregar espaços:", error)
        }
    }

    const toggleAmenity = (id: string) => {
        setSelectedAmenities(prev =>
            prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
        )
    }

    const handleSearch = async () => {
        if (!currentOrg || !selectedDate) return
        setLoading(true)

        try {
            // 1. Definir janelas de tempo
            const startDateTime = new Date(selectedDate)
            const [startH, startM] = startTime.split(':')
            startDateTime.setHours(parseInt(startH), parseInt(startM), 0, 0)

            const endDateTime = new Date(selectedDate)
            const [endH, endM] = endTime.split(':')
            endDateTime.setHours(parseInt(endH), parseInt(endM), 0, 0)

            if (endDateTime <= startDateTime) {
                alert("O horário de término deve ser após o início.")
                setLoading(false)
                return
            }

            // 2. Buscar reservas existentes para o dia
            // Para simplificar e evitar erros de índice composto sem aviso, buscamos todas do dia
            const dayStart = new Date(selectedDate)
            dayStart.setHours(0, 0, 0, 0)
            const dayEnd = new Date(selectedDate)
            dayEnd.setHours(23, 59, 59, 999)

            const q = query(
                collection(db, 'bookings'),
                where('orgId', '==', currentOrg.id),
                where('start', '>=', Timestamp.fromDate(dayStart)),
                where('start', '<=', Timestamp.fromDate(dayEnd))
            )
            const snapshot = await getDocs(q)
            const dayBookings = snapshot.docs.map(doc => doc.data() as Booking)

            // 3. Filtrar espaços
            const filtered = spaces.filter(space => {
                // Filtro de Capacidade
                if (space.capacity < capacity) return false

                // Filtro de Comodidades
                const hasAllAmenities = selectedAmenities.every(a =>
                    space.amenities?.some(sa => sa.toLowerCase().includes(a.toLowerCase()))
                )
                if (!hasAllAmenities) return false

                // Filtro de Disponibilidade (Cruzamento de Agenda)
                const isConflict = dayBookings.some(b => {
                    if (b.spaceId !== space.id || b.status === 'CANCELED') return false

                    const bStart = b.start.toDate()
                    const bEnd = b.end.toDate()

                    // Lógica de sobreposição: (Max(Start1, Start2) < Min(End1, End2))
                    const overlap = Math.max(startDateTime.getTime(), bStart.getTime()) <
                        Math.min(endDateTime.getTime(), bEnd.getTime())
                    return overlap
                })

                return !isConflict
            })

            setAvailableSpaces(filtered)
        } catch (error) {
            console.error("Erro na busca:", error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="w-full mx-auto p-0 space-y-4 page-enter">
            {/* Filters Section */}
            <div className="bg-white p-8 rounded-[2.5rem] border border-[#001c28]/5 shadow-sm space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Data */}
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-[#001c28]/60 uppercase tracking-[0.3em] flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-2 text-[#d4a94d]" /> Data da Reserva
                        </label>
                        <DatePicker
                            selected={selectedDate}
                            onChange={(date: Date | null) => setSelectedDate(date)}
                            dateFormat="dd/MM/yyyy"
                            locale={ptBR}
                            className="w-full bg-[#001c28]/[0.02] border border-[#001c28]/10 focus:border-[#d4a94d] focus:ring-2 focus:ring-[#d4a94d]/20 rounded-xl px-4 py-3 text-[#001c28] font-bold transition-all outline-none"
                            placeholderText="Selecione o dia"
                        />
                    </div>

                    {/* Horários */}
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-[#001c28]/60 uppercase tracking-[0.3em] flex items-center">
                            <Clock className="w-4 h-4 mr-2 text-[#d4a94d]" /> Intervalo
                        </label>
                        <div className="flex items-center space-x-2">
                            <input
                                type="time"
                                value={startTime}
                                onChange={e => setStartTime(e.target.value)}
                                className="w-full bg-[#001c28]/[0.02] border border-[#001c28]/10 focus:border-[#d4a94d] focus:ring-2 focus:ring-[#d4a94d]/20 rounded-xl px-3 py-3 text-[#001c28] font-bold transition-all outline-none text-center"
                            />
                            <span className="text-[#001c28]/20 font-black">/</span>
                            <input
                                type="time"
                                value={endTime}
                                onChange={e => setEndTime(e.target.value)}
                                className="w-full bg-[#001c28]/[0.02] border border-[#001c28]/10 focus:border-[#d4a94d] focus:ring-2 focus:ring-[#d4a94d]/20 rounded-xl px-3 py-3 text-[#001c28] font-bold transition-all outline-none text-center"
                            />
                        </div>
                    </div>

                    {/* Capacidade */}
                    <div className="space-y-3">
                        <label className="text-[10px] font-black text-[#001c28]/60 uppercase tracking-[0.3em] flex items-center">
                            <Users className="w-4 h-4 mr-2 text-[#d4a94d]" /> Capacidade Mín.
                        </label>
                        <div className="relative pt-2">
                            <input
                                type="range"
                                min="1"
                                max="100"
                                value={capacity}
                                onChange={e => setCapacity(parseInt(e.target.value))}
                                className="w-full h-2 bg-[#001c28]/5 rounded-lg appearance-none cursor-pointer accent-[#d4a94d]"
                            />
                            <div className="flex justify-between mt-3">
                                <span className="text-[10px] font-black text-[#001c28]">{capacity} pessoas</span>
                                <span className="text-[10px] font-black text-[#001c28]/30">100+</span>
                            </div>
                        </div>
                    </div>

                    {/* Botão de Busca */}
                    <div className="flex items-end">
                        <button
                            onClick={handleSearch}
                            disabled={loading}
                            className="w-full bg-[#001c28] text-white hover:text-[#d4a94d] font-black py-4 rounded-xl uppercase tracking-widest text-[10px] shadow-lg shadow-[#001c28]/20 hover:bg-[#001c28]/90 active:scale-95 transition-all flex items-center justify-center space-x-3 group"
                        >
                            {loading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <>
                                    <Search className="w-4 h-4" />
                                    <span>Buscar Disponíveis</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Amenities */}
                <div className="pt-6 border-t border-[#001c28]/5 space-y-4">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-black text-[#001c28]/60 uppercase tracking-[0.3em]">Comodidades Disponíveis</label>
                        {(selectedAmenities.length > 0 || searchTerm || capacity > 1) && (
                            <button
                                onClick={() => {
                                    setSelectedAmenities([])
                                    setSearchTerm('')
                                    setCapacity(1)
                                    setStartTime('08:00')
                                    setEndTime('18:00')
                                    setSelectedDate(new Date())
                                }}
                                className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-wider transition-colors"
                            >
                                Limpar Filtros
                            </button>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {Array.from(new Set(spaces.flatMap(s => s.amenities || []).map(a => a.trim()))).sort().map((amenity) => {
                            const isSelected = selectedAmenities.includes(amenity)
                            return (
                                <button
                                    key={amenity}
                                    onClick={() => toggleAmenity(amenity)}
                                    className={`flex items-center space-x-2 px-5 py-3 rounded-xl border-2 transition-all group ${isSelected
                                        ? 'bg-[#001c28] border-[#001c28] text-[#d4a94d] shadow-lg shadow-[#001c28]/20'
                                        : 'bg-white border-[#001c28]/5 text-[#001c28]/60 hover:border-[#d4a94d] hover:text-[#001c28]'
                                        }`}
                                >
                                    <span className="text-[10px] font-black uppercase tracking-widest">{amenity}</span>
                                </button>
                            )
                        })}
                        {spaces.length > 0 && spaces.flatMap(s => s.amenities || []).length === 0 && (
                            <p className="text-xs text-gray-400 italic">Nenhuma comodidade cadastrada nos espaços.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
                <div className="flex items-center space-x-4">
                    <LayoutGrid className="w-7 h-7 text-[#d4a94d]" />
                    <h2 className="text-2xl font-black text-[#001c28] uppercase tracking-tighter italic">Espaços Encontrados ({availableSpaces.length})</h2>
                </div>

                {availableSpaces.length === 0 ? (
                    <div className="bg-white rounded-[2.5rem] p-12 text-center border border-[#001c28]/5 shadow-sm">
                        <div className="w-24 h-24 bg-[#001c28]/5 rounded-2xl flex items-center justify-center mx-auto mb-8 border-2 border-[#001c28]/10">
                            <Info className="w-10 h-10 text-[#001c28]/20" />
                        </div>
                        <h3 className="text-xl font-black text-[#001c28] uppercase italic mb-2">Inicie uma nova busca</h3>
                        <p className="text-[#001c28]/50 font-medium text-sm">Ajuste os filtros acima para exibir os espaços disponíveis.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {availableSpaces.map(space => (
                            <Link
                                to={`/bookings/new?spaceId=${space.id}&date=${selectedDate?.toISOString()}&start=${startTime}&end=${endTime}`}
                                key={space.id}
                                className="bg-white rounded-[2.5rem] p-0 group hover:-translate-y-2 transition-all duration-500 overflow-hidden relative border border-[#001c28]/5 hover:border-[#d4a94d]/30"
                            >
                                <div className="absolute -right-4 -top-4 w-28 h-28 bg-[#d4a94d]/10 rounded-full blur-2xl group-hover:bg-[#d4a94d]/20 transition-colors" />
                                <div className="relative z-10">
                                    <div className="aspect-[16/10] bg-[#001c28]/5 relative overflow-hidden rounded-t-[2.5rem]">
                                        {space.photos?.[0] ? (
                                            <img src={space.photos[0]} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-[#001c28]/10">
                                                <LayoutGrid className="w-12 h-12" />
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4 flex space-x-2">
                                            <span className="bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg text-[9px] font-black text-[#001c28] uppercase tracking-widest shadow-lg border border-[#001c28]/10">{RESOURCE_TYPE_LABELS[space.type] || space.type}</span>
                                        </div>
                                        <div className="absolute bottom-4 right-4">
                                            <div className="bg-[#001c28] text-white px-4 py-2 rounded-xl shadow-xl flex items-center space-x-2 border border-white/10">
                                                <Users className="w-4 h-4 text-[#d4a94d]" />
                                                <span className="text-xs font-black">{space.capacity}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-6 space-y-4">
                                        <h3 className="text-xl font-black text-[#001c28] uppercase tracking-tight group-hover:text-[#d4a94d] transition-colors">{space.name}</h3>
                                        <div className="flex flex-wrap gap-2">
                                            {space.amenities?.slice(0, 3).map(a => (
                                                <span key={a} className="text-[9px] font-black text-[#001c28]/60 uppercase tracking-widest bg-[#001c28]/5 px-3 py-1.5 rounded-lg border border-[#001c28]/5">{a}</span>
                                            ))}
                                        </div>
                                        <div className="pt-4 border-t border-[#001c28]/5 flex items-center justify-between">
                                            <div className="flex flex-col">
                                                <span className="text-[9px] font-black text-[#001c28]/40 uppercase tracking-widest">A partir de</span>
                                                <span className="text-xl font-black text-[#001c28]">R$ {space.pricePerHour || 0}<span className="text-xs text-[#001c28]/40">/h</span></span>
                                            </div>
                                            <div className="w-12 h-12 rounded-xl bg-[#001c28]/5 text-[#001c28] flex items-center justify-center group-hover:bg-[#d4a94d] group-hover:text-[#001c28] transition-all shadow-lg">
                                                <ArrowRight className="w-6 h-6" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
